<script>
import BaseButton from '../_base-button.vue'
import BaseIcon from '../_base-icon.vue'
import paywallTracker from '@services/mixpanel/paywall-tracker'
import { CTA_TYPES, DISMISS_METHODS } from '@services/mixpanel/analytics-spec'

export default {
    name: 'PremiumUpsellModal',

    components: {
        BaseButton,
        BaseIcon,
    },

    props: {
        data: {
            type: Object,
            default: () => ({}),
        },
    },

    computed: {
        headline() {
            return this.data.headline
        },
        description() {
            return this.data.description
        },
        subDescription() {
            return this.data.subDescription
        },
        feature() {
            return this.data.feature
        },
        primaryAction() {
            return this.data.primaryAction
        },
        secondaryAction() {
            return this.data.secondaryAction
        },
    },

    methods: {
        close(dismissMethod = DISMISS_METHODS.CLOSE) {
            paywallTracker.onWallDismissed(dismissMethod)
            this.$emit('close')
        },
        _closeWithoutTracking() {
            this.$emit('close')
        },
        handlePrimaryAction() {
            paywallTracker.onCtaTapped(this._getCTAType(this.primaryAction))
            if (this.primaryAction.route) {
                this.$router.push(this.primaryAction.route)
            }
            if (this.primaryAction.callback) {
                this.primaryAction.callback()
            }
            this._closeWithoutTracking()
        },
        handleSecondaryAction() {
            if (!this.secondaryAction) return

            paywallTracker.onCtaTapped(this._getCTAType(this.secondaryAction))
            if (this.secondaryAction.route) {
                this.$router.push(this.secondaryAction.route)
            }
            if (this.secondaryAction.callback) {
                this.secondaryAction.callback()
            }
            this._closeWithoutTracking()
        },
        _getCTAType(action) {
            if (!action) return CTA_TYPES.UPGRADE
            const text = action.text?.toLowerCase() || ''
            if (text.includes('trial')) return CTA_TYPES.START_TRIAL
            if (text.includes('restore')) return CTA_TYPES.RESTORE
            if (text.includes('plan')) return CTA_TYPES.VIEW_PLANS
            return CTA_TYPES.UPGRADE
        },
    },
}
</script>

<template>
    <div :class="$style.modal">
        <button
            :class="$style.closeButton"
            aria-label="Close modal"
            @click="close"
        >
            <svg
                viewBox="0 0 24 24"
                fill="none"
            >
                <path
                    d="M18 6L6 18"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                />
                <path
                    d="M6 6L18 18"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                />
            </svg>
        </button>

        <div :class="$style.content">
            <BaseIcon
                :class="$style.premiumLogo"
                name="premium-logo"
            />

            <h2 :class="$style.headline">{{ headline }}</h2>

            <p
                v-if="subDescription"
                :class="$style.subDescription"
            >{{ subDescription }}</p>

            <div :class="$style.actions">
                <BaseButton
                    :class="$style.primaryButton"
                    size="large"
                    button-type="primaryFlat"
                    @click="handlePrimaryAction"
                >
                    {{ primaryAction.text }}
                </BaseButton>

                <button
                    v-if="secondaryAction"
                    :class="$style.secondaryLink"
                    @click="handleSecondaryAction"
                >
                    {{ secondaryAction.text }}
                </button>
            </div>
        </div>
    </div>
</template>

<style module lang="scss">
@use '@design' as *;

.modal {
    position: relative;
    width: 90vw;
    max-width: 710px;
    padding: $s-h 64px 48px;
    background: #27292D;
    border: none;
    border-radius: 12px;
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.95);

    @include mobileOnly {
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: calc(100vw - $s-xxl);
        max-width: none;
        height: auto;
        padding: $s-xxl 30px 36px;
        margin: $s-l;
        border-radius: $s-m;
    }
}

.closeButton {
    position: absolute;
    top: $s-l;
    right: $s-l;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: $s-xxs;
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    background: transparent;
    border: none;
    border-radius: 50%;
    transition: all 0.2s ease;

    svg {
        width: $s-l;
        height: $s-l;
    }

    &:hover {
        color: rgba(255, 255, 255, 0.8);
        background: rgba(255, 255, 255, 0.08);
    }

    @include mobileOnly {
        top: $s-l;
        right: $s-l;
    }
}

.content {
    display: flex;
    flex-direction: column;
    gap: 0;
    align-items: flex-start;
    text-align: left;
}

.premiumLogo {
    display: block;
    width: 180px;
    height: 45px;
    margin-bottom: 32px;
    fill: $c-razzmatazz;

    @include mobileOnly {
        width: 144px;
        height: 36px;
        margin-bottom: $s-xl;
    }
}

.headline {
    margin: 0;
    margin-bottom: $s-xl;
    font-family: aktiv-grotesk-extended, -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: 35px;
    font-weight: 700;
    line-height: 1.2;
    color: $c-white;
    text-align: left;
    letter-spacing: -0.03em;
    white-space: pre-wrap;

    @include mobileOnly {
        margin-bottom: $s-l;
        font-size: 27px;
        line-height: 1.15;
    }
}

.description {
    @extend %body;

    max-width: 320px;
    margin: 0;
    font-size: 16px;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.7);
}

.subDescription {
    margin: 0;
    margin-top: -$s-m;
    margin-bottom: 140px;
    font-family: aktiv-grotesk-extended, -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: 35px;
    font-weight: 700;
    line-height: 1.2;
    color: $c-white;
    text-align: left;
    letter-spacing: -0.03em;
    white-space: pre-wrap;

    @include mobileOnly {
        margin-top: -$s-s;
        margin-bottom: 80px;
        font-size: 27px;
        line-height: 1.15;
    }
}

.actions {
    display: flex;
    flex-direction: column;
    gap: 1px;
    align-items: center;
    width: 100%;
    margin-top: $s-xs;

    @include mobileOnly {
        flex-direction: row;
        flex-wrap: wrap;
        gap: $s-m;
        align-items: center;
        justify-content: center;
        margin-top: $s-xl;
    }
}

.primaryButton {
    width: 100%;
    max-width: 400px;
    padding: $s-l 36px;
    font-size: $s-m;
    text-transform: none;
    letter-spacing: normal;
    border-radius: 50px;

    @include mobileOnly {
        flex: 0 0 auto;
        width: auto;
        padding: $s-m 28px;
    }
}

.secondaryLink {
    @extend %p;

    align-self: center;
    font-size: 15px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.7);
    text-align: center;
    text-decoration: none;
    letter-spacing: 0.01em;
    cursor: pointer;
    background: transparent;
    border: none;
    transition: all 0.2s ease;

    @include mobileOnly {
        flex-shrink: 0;
        align-self: center;
        padding: 0 $s-xs;
        margin-top: 0;
        font-size: 14px;
        white-space: nowrap;
    }

    &:hover {
        font-weight: 700;
        color: rgba(255, 255, 255, 0.95);
    }
}
</style>
